import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { ScoreResultType } from "@/modules/scores/scoreTypes"
import { BlockSessionInputDtoSchema, SessionBlockDtoSchema } from "@/modules/sessions/gateway/sessionBlockDto"
import { SocialPostResumeLightDtoSchema } from "@/modules/social/gateway/socialDto"
import { z } from "zod"

/* DTO object returned by :
 * GET  web/sessions/{sessionId}
 * POST web/planning/sessions
 * PUT  web/sessions/{sessionId}
 */
export const WebSessionDetailsDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    date: z.string().nullable(),
    owner: UserDtoSchema,
    programId: z.string().nullable(),
    programVisibility: z.enum(ProgramVisibility).nullable(),
    programName: z.string().nullable(),
    hideSessionContent: z.boolean().nullable(),
    weightliftingTag: z.boolean(),
    gymnasticTag: z.boolean(),
    cardioTag: z.boolean(),
    index: z.number().nullable(),
    virtualProgramByDateIndex: z.number().nullable(),
    programType: z.enum(ProgramType).nullable(),
    socialPostId: z.string().nullable(),
    socialResume: SocialPostResumeLightDtoSchema.nullable(),
    blocks: z.array(SessionBlockDtoSchema),
    sessionCompletedByUser: z.boolean(),
  })
  .meta({ id: "WebSessionDetailsDtoSchema" })

// DTO use for program infinite and pro
export const WebSessionDetailsDtoWithNonNullableSchema = WebSessionDetailsDtoSchema.extend({
  programId: z.string(),
  programName: z.string(),
  programVisibility: z.enum(ProgramVisibility),
  programType: z.enum(ProgramType),
}).meta({ id: "WebSessionDetailsDtoWithNonNullableSchema" })

/* DTO object returned by :
 * GET  web/planning/sessions
 */
export const WebSessionsPlanningDtoSchema = z
  .object({
    sessions: z.array(WebSessionDetailsDtoWithNonNullableSchema),
  })
  .meta({ id: "WebSessionsPlanningDtoSchema" })

/* DTO object expected by :
 * POST web/planning/sessions
 */
export const CreateSessionInputDtoSchema = z
  .object({
    programId: z.string(),
    name: z.string().min(0).max(64),
    description: z.string().min(0).max(65535).nullable(),
    date: z.string(),
    duration: z.number(),
    blocks: z.array(BlockSessionInputDtoSchema),
    hideSessionContent: z.boolean().nullable(),
    weightliftingTag: z.boolean(),
    gymnasticTag: z.boolean(),
    cardioTag: z.boolean(),
    restDay: z.boolean(),
    index: z.number().nullable(),
    scoringOption: z.enum(ScoreResultType).nullable(),
  })
  .meta({ id: "CreateSessionInputDtoSchema" })

/* DTO object expected by :
 * PUT  web/sessions/{sessionId}
 */
export const UpdateSessionInputDtoSchema = z
  .object({
    name: z.string().min(0).max(64),
    description: z.string().min(0).max(65535).nullable(),
    date: z.string().nullable(),
    duration: z.number(),
    blocks: z.array(BlockSessionInputDtoSchema),
    hideSessionContent: z.boolean().nullable(),
    weightliftingTag: z.boolean(),
    gymnasticTag: z.boolean(),
    cardioTag: z.boolean(),
    restDay: z.boolean(),
    index: z.number().nullable(),
    virtualProgramByDateIndex: z.number().nullable(),
  })
  .meta({ id: "UpdateSessionInputDtoSchema" })

export type WebSessionDetailsDto = z.infer<typeof WebSessionDetailsDtoSchema>
export type WebSessionDetailsDtoWithNonNullable = z.infer<typeof WebSessionDetailsDtoWithNonNullableSchema>
export type WebSessionsPlanningDto = z.infer<typeof WebSessionsPlanningDtoSchema>
export type CreateSessionInputDto = z.infer<typeof CreateSessionInputDtoSchema>
export type UpdateSessionInputDto = z.infer<typeof UpdateSessionInputDtoSchema>
